import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"

export async function getVoiceToken(
  tokenInfo: ITokenInfo
): Promise<{ data: { identity: string; token: string } }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/voice/token`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export interface IVoiceNumber {
  phone_number: string
  country_code: string | null
  region_code: string | null
  status: string
  daily_cap: number
  day_count: number
  cooldown_until: string | null
  // Health columns the pool carries. Null until the spam/health job has
  // scored the number, which is why the UI must treat them as unknown
  // rather than as zero.
  answer_rate: number | null
  health_score: number | null
  spam_state: string | null
}

export async function setCallHold(
  tokenInfo: ITokenInfo,
  callSid: string,
  hold: boolean
): Promise<{ data: { hold: boolean } }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/voice/calls/${callSid}/hold`

  return await axiosInstance({
    method: "POST",
    url,
    data: { hold },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function sendCallToVoicemail(
  tokenInfo: ITokenInfo,
  callSid: string
): Promise<{ data: { voicemail: boolean } }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/voice/calls/${callSid}/voicemail`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function declineIncomingCall(
  tokenInfo: ITokenInfo,
  callSid: string
): Promise<{ data: { declined: boolean } }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/voice/calls/${callSid}/decline`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getVoiceNumbers(
  tokenInfo: ITokenInfo
): Promise<{ data: { numbers: IVoiceNumber[] } }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/voice/numbers`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

// Put a caller-ID number into rotation or take it out. status is the only field
// selectCallerNumber filters on, so this decides whether calls ever go out on
// it. dialer-service refuses to activate a number the Twilio account no longer
// holds, answering 409 — a pool row is not proof of ownership.
export async function setVoiceNumberStatus(
  tokenInfo: ITokenInfo,
  phoneNumber: string,
  status: "active" | "inactive"
): Promise<{ data: { number: IVoiceNumber } }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/voice/numbers/${encodeURIComponent(phoneNumber)}`

  return await axiosInstance({
    method: "PATCH",
    url,
    data: { status },
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}
